// ─────────────────────────────────────────────────────────────
// Conditional logger — silent in production
// ─────────────────────────────────────────────────────────────
export const log = process.env.NODE_ENV === 'development'
  ? console.log.bind(console)
  : () => {};

// ─────────────────────────────────────────────────────────────
// Convert a raw APS URN to a URL-safe base-64 string.
// ─────────────────────────────────────────────────────────────
export const toUrlSafeUrn = (rawUrn = '') => {
  if (!rawUrn) {
    console.warn('[toUrlSafeUrn] Empty or null URN provided');
    return '';
  }
  try {
    return btoa(rawUrn)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  } catch (error) {
    console.error('[toUrlSafeUrn] Failed to encode URN:', error);
    return '';
  }
};

// ─────────────────────────────────────────────────────────────
// Extract the conductor name (3rd segment) from a ";;" string.
// ─────────────────────────────────────────────────────────────
export const extractConductorName = (fullString) => {
  if (!fullString) return fullString;
  return fullString.split(';;')[2];
};

// ─────────────────────────────────────────────────────────────
// Traverse the APS instance tree and collect all leaf dbIds.
// ─────────────────────────────────────────────────────────────
export const getLeafDbIds = (instanceTree) => {
  const leafDbIds = [];
  instanceTree.enumNodeChildren(
    instanceTree.getRootId(),
    (dbId) => {
      if (instanceTree.getChildCount(dbId) === 0) leafDbIds.push(dbId);
    },
    true,
  );
  return leafDbIds;
};

// ─────────────────────────────────────────────────────────────
// Shared eligibility guard used by both the confirm modal and
// the API request builder — single source of truth.
// ─────────────────────────────────────────────────────────────
export const isAssetEligible = (asset, formId) =>
  asset.selected && (!asset.formId || asset.formId === 0 || asset.formId === formId);

// ─────────────────────────────────────────────────────────────
// Resolve the correct viewer error message.
// ─────────────────────────────────────────────────────────────
export const getViewerErrorMessage = ({ error, urlSafeUrn, urn }) => {
  if (error)               return error;
  if (!urlSafeUrn && urn)  return 'Invalid URN format. Unable to load 3D model.';
  if (!urlSafeUrn)         return 'No 3D model URN provided. Please select a drawing.';
  return 'Viewer credentials unavailable.';
};

// ─────────────────────────────────────────────────────────────
// getPlatform — detects whether the user is on Mac or Windows.
// Uses navigator.userAgentData (modern) with a fallback to
// navigator.platform (legacy) so it works across all browsers.
// Returns 'mac' | 'windows' | 'other'.
// ─────────────────────────────────────────────────────────────
export const getPlatform = () => {
  // Modern API — available in Chrome 90+, Edge 90+
  if (navigator.userAgentData?.platform) {
    const p = navigator.userAgentData.platform.toLowerCase();
    if (p.includes('mac'))  return 'mac';
    if (p.includes('win'))  return 'windows';
    return 'other';
  }
  // Legacy fallback — still works in Firefox and Safari
  const p = (navigator.platform ?? '').toLowerCase();
  if (p.startsWith('mac'))  return 'mac';
  if (p.startsWith('win'))  return 'windows';
  return 'other';
};

